const norm = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const firstText = (...values) => values.find((value) => String(value || '').trim())?.toString().trim() || '';

const splitAddressString = (value) => String(value || '')
  .split(/\s*·\s*|\r?\n+/)
  .map((line) => line.trim())
  .filter(Boolean);

const addressObjectLines = (addr = {}) => ([
  firstText(addr.name),
  firstText(addr.company, addr.companyName),
  firstText(addr.street, addr.addressLine1),
  firstText(addr.street2, addr.addressLine2),
  [addr.city, addr.state || addr.stateProvince].filter(Boolean).join(', '),
  [addr.postal || addr.postalCode, addr.country].filter(Boolean).join(' '),
]).filter(Boolean);

const addressLines = (addr) => {
  if (!addr) return [];
  if (typeof addr === 'string') return splitAddressString(addr);
  return addressObjectLines(addr);
};

const uniqueLines = (lines) => {
  const seen = new Set();
  return lines.filter((line) => {
    const key = norm(line);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeToken = (value) => norm(value).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const hasGoodsToDecorationLink = (linkedPurchaseOrders = []) =>
  Array.isArray(linkedPurchaseOrders) &&
  linkedPurchaseOrders.some((linked) => normalizeToken(linked?.relationship) === 'goods_to_decoration');

const isSupplierToDecoratorRoute = ({
  supplierInfo = {},
  routeSummary = '',
  linkedPurchaseOrders = [],
} = {}) => {
  const normalizedRoute = norm(routeSummary || supplierInfo?.routeSummary);
  if (hasGoodsToDecorationLink(linkedPurchaseOrders || supplierInfo?.linkedPurchaseOrders)) {
    return true;
  }
  return normalizedRoute === 'supplier → decorator' || normalizedRoute === 'supplier -> decorator';
};

const getDestinationOrganizationName = (addr = {}) =>
  firstText(addr?.name, addr?.company, addr?.companyName);

export const getPurchaseOrderShippingRecipient = ({
  supplierInfo = {},
  shipTo = {},
  shippingContact = null,
  shippingName = '',
  linkedPurchaseOrders = [],
  routeSummary = '',
} = {}) => {
  const destinationOrganizationName = getDestinationOrganizationName(shipTo);

  if (
    isSupplierToDecoratorRoute({
      supplierInfo,
      routeSummary,
      linkedPurchaseOrders: linkedPurchaseOrders.length ? linkedPurchaseOrders : supplierInfo?.linkedPurchaseOrders,
    })
  ) {
    return firstText(
      destinationOrganizationName,
      shipTo?.street,
      shipTo?.city,
      shippingContact?.name,
      shippingName
    );
  }

  return firstText(
    shipTo?.contactName,
    shippingContact?.name,
    supplierInfo?.shippingContact?.name,
    supplierInfo?.shippingName,
    shippingName,
    shipTo?.attn,
    shipTo?.attention,
    shipTo?.contact
  );
};

export const getPoAddressContactName = (po = {}, addr = {}, type = 'shipping') => {
  const isBilling = type === 'billing';
  const supplierInfo = po?.supplierInfo || {};
  if (!isBilling) {
    return getPurchaseOrderShippingRecipient({
      supplierInfo,
      shipTo: addr,
      shippingContact: supplierInfo?.shippingContact,
      shippingName: firstText(
        po?.shippingContactName,
        po?.salesOrder?.shippingName,
        supplierInfo?.shippingName,
        po?.shippingName
      ),
      linkedPurchaseOrders: supplierInfo?.linkedPurchaseOrders,
      routeSummary: po?.routeSummary || supplierInfo?.routeSummary,
    });
  }
  return firstText(
    addr?.contactName,
    isBilling ? supplierInfo?.billingContact?.name : supplierInfo?.shippingContact?.name,
    isBilling ? po?.billingContactName : po?.shippingContactName,
    isBilling ? po?.salesOrder?.billingName : po?.salesOrder?.shippingName,
    isBilling ? supplierInfo?.billingName : supplierInfo?.shippingName,
    isBilling ? po?.billingName : po?.shippingName,
    addr?.attn,
    addr?.attention,
    addr?.contact
  );
};

export const buildPoAddressBlock = (po = {}, addr = {}, type = 'shipping') => {
  const contactName = getPoAddressContactName(po, addr, type);
  const baseLines = uniqueLines(addressLines(addr));
  const lines = baseLines.filter((line) => {
    const normalizedLine = norm(line);
    if (contactName && normalizedLine === norm(contactName)) return false;
    return true;
  });

  return { contactName, lines };
};
